import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Home, ScrollText, User, CalendarDays } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const BottomNavStyles = () => (
  <style>{`
    .peaches-bottom-nav {
      position: fixed;
      bottom: 0;
      left: 0;
      width: 100%;
      height: 64px;
      background: #fff;
      display: flex;
      justify-content: space-around;
      align-items: center;
      border-top: 1px solid #e5e5e5;
      z-index: 500;
      padding: 0 1rem;
      box-sizing: border-box;
    }

    .peaches-nav-btn {
      background: none;
      border: none;
      cursor: pointer;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 4px;
      color: #999;
      font-family: 'Plus Jakarta Sans', 'Noto Sans TC', sans-serif;
      font-size: 0.7rem;
      font-weight: 400;
      padding: 8px 12px;
      border-radius: 10px;
      text-decoration: none;
      flex: 1;
      max-width: 88px;
      min-height: 48px;
      transition: color 0.15s ease;
    }

    .peaches-nav-btn svg {
      width: 22px;
      height: 22px;
      flex-shrink: 0;
    }

    .peaches-nav-btn span {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      width: 100%;
      text-align: center;
    }

    @media (min-width: 769px) {
      .peaches-nav-btn:hover {
        color: #2F7D5E;
      }
    }

    .peaches-nav-btn.active {
      color: #2F7D5E;
      font-weight: 400;
    }

    /* Small phones - hide text */
    @media (max-width: 360px) {
      .peaches-nav-btn span {
        display: none;
      }

      .peaches-nav-btn {
        gap: 0;
      }

      .peaches-bottom-nav {
        height: 56px;
      }
    }

    /* Landscape */
    @media (max-height: 600px) and (orientation: landscape) {
      .peaches-bottom-nav {
        height: 52px;
      }

      .peaches-nav-btn {
        padding: 6px 10px;
        font-size: 0.65rem;
        min-height: 40px;
      }

      .peaches-nav-btn svg {
        width: 20px;
        height: 20px;
      }
    }

    @media (prefers-reduced-motion: reduce) {
      .peaches-nav-btn {
        transition: none !important;
      }
    }

    body {
      padding-bottom: 64px;
    }

    @media (max-width: 360px) {
      body {
        padding-bottom: 56px;
      }
    }

    @media (max-height: 600px) and (orientation: landscape) {
      body {
        padding-bottom: 52px;
      }
    }
  `}</style>
);

const BottomNav = ({ isLoggedIn, requestLogin, setPendingRoute, onGoHome }) => {
  const { t } = useTranslation('home');
  const location = useLocation();
  const langMatch = location.pathname.match(/^\/(en)(\/|$)/);
  const langPrefix = langMatch ? `/${langMatch[1]}` : '';

  const handleProtectedClick = (event, route) => {
    if (!isLoggedIn) {
      event.preventDefault();
      setPendingRoute(`${langPrefix}${route}`);
      requestLogin();
    }
  };

  return (
    <>
      <BottomNavStyles />
      <nav className="peaches-bottom-nav" role="navigation" aria-label={t('nav.aria_main')}>
        <NavLink
          to={`${langPrefix}/`}
          end
          className={({ isActive }) => isActive ? 'peaches-nav-btn active' : 'peaches-nav-btn'}
          onClick={onGoHome}
          aria-label={t('nav.aria_home')}
        >
          <Home />
          <span>{t('nav.home')}</span>
        </NavLink>

        <NavLink
          to={`${langPrefix}/schedule`}
          className={({ isActive }) => isActive ? 'peaches-nav-btn active' : 'peaches-nav-btn'}
          onClick={(e) => handleProtectedClick(e, '/schedule')}
          aria-label={t('nav.aria_schedule')}
        >
          <CalendarDays />
          <span>{t('nav.schedule')}</span>
        </NavLink>

        <NavLink
          to={`${langPrefix}/orders`}
          className={({ isActive }) => isActive ? 'peaches-nav-btn active' : 'peaches-nav-btn'}
          onClick={(e) => handleProtectedClick(e, '/orders')}
          aria-label={t('nav.aria_orders')}
        >
          <ScrollText />
          <span>{t('nav.orders')}</span>
        </NavLink>

        <NavLink
          to={`${langPrefix}/account`}
          className={({ isActive }) => isActive ? 'peaches-nav-btn active' : 'peaches-nav-btn'}
          onClick={(e) => handleProtectedClick(e, '/account')}
          aria-label={t('nav.aria_account')}
        >
          <User />
          <span>{t('nav.account')}</span>
        </NavLink>
      </nav>
    </>
  );
};

export default BottomNav;
